import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, type AdminSubscriptionTariff } from '@/services/api';
import { Loader2 } from 'lucide-react';

const PLAN_DEFAULT_DURATIONS: Record<string, number> = {
  pro_1m: 1,
  pro_6m: 6,
  pro_12m: 12,
};

export interface GrantProModalProps {
  storeId: string;
  storeName?: string | null;
  onClose: () => void;
  onGranted?: () => void;
}

export function GrantProModal({ storeId, storeName, onClose, onGranted }: GrantProModalProps) {
  const [tariffs, setTariffs] = useState<AdminSubscriptionTariff[]>([]);
  const [planCode, setPlanCode] = useState<string>('pro_1m');
  const [durationMonths, setDurationMonths] = useState<number>(1);
  const [notes, setNotes] = useState<string>('');
  const [loadingTariffs, setLoadingTariffs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingTariffs(true);
    api
      .getSubscriptionTariffs()
      .then(({ tariffs: list }) => {
        if (cancelled) return;
        setTariffs(list);
        if (list.length > 0) {
          setPlanCode(list[0].code);
          setDurationMonths(list[0].durationMonths || 1);
        }
      })
      .catch(() => {
        if (!cancelled) setTariffs([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTariffs(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handlePlanChange = (code: string) => {
    setPlanCode(code);
    const tariff = tariffs.find((t) => t.code === code);
    if (tariff) setDurationMonths(tariff.durationMonths);
    else setDurationMonths(PLAN_DEFAULT_DURATIONS[code] || 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.grantProSubscription(storeId, { planCode, durationMonths, notes: notes || undefined });
      onGranted?.();
      onClose();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setError(msg || (err instanceof Error ? err.message : 'Не удалось выдать Pro'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-lg font-semibold">Выдать Pro</h3>
          <p className="text-sm text-muted-foreground">
            Магазин: <span className="font-medium">{storeName || storeId}</span>
          </p>

          {loadingTariffs ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Загрузка тарифов...
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Тариф</Label>
                <select
                  value={planCode}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm mt-1"
                >
                  {tariffs.length === 0 ? (
                    <>
                      <option value="pro_1m">1 месяц</option>
                      <option value="pro_6m">6 месяцев</option>
                      <option value="pro_12m">1 год</option>
                    </>
                  ) : (
                    tariffs.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.title || t.code}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <Label>Длительность (мес)</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Заметка (необязательно)</Label>
                <Input
                  type="text"
                  placeholder="Например: промо-выдача"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Отмена
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || loadingTariffs}>
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
              Выдать Pro
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
